import { Suspense, lazy, useEffect, useState } from 'react';

const importLottie = () => import('./LottieInner');
const LottieInner = lazy(importLottie);

// Prefetch the lazy Lottie chunk before the first spinner mounts — the boot
// splash renders across a hard OIDC redirect, and a cold chunk shows the empty
// placeholder before the mark paints. Idempotent; HTTP-cached after first fetch.
export function preloadGlobalSpinner(): void {
  void importLottie();
}

type SpinnerSize = 'xs' | 'sm' | 'md' | 'lg' | 'xl' | '2xl' | '3xl' | '4xl';

const SIZE_PIXELS: Record<SpinnerSize, number> = {
  xs: 20,
  sm: 24,
  md: 32,
  lg: 48,
  xl: 64,
  '2xl': 96,
  '3xl': 192,
  '4xl': 336,
};

interface Props {
  // A named scale step, or an explicit pixel size for one-off dimensions (e.g.
  // the boot splash's 200px brand mark).
  size?: SpinnerSize | number;
  /** Omit to follow the app theme (the `.dark` class on <html>). */
  theme?: 'light' | 'dark';
  className?: string;
  speed?: number;
}

function rootIsDark(): boolean {
  return typeof document !== 'undefined' && document.documentElement.classList.contains('dark');
}

// The Lottie mark is baked-color artwork, not token-driven CSS — it must be
// swapped per theme or it disappears against the matching background.
function useResolvedTheme(theme?: 'light' | 'dark'): 'light' | 'dark' {
  const [isDark, setIsDark] = useState(rootIsDark);
  useEffect(() => {
    if (theme || typeof document === 'undefined') return;
    const root = document.documentElement;
    const observer = new MutationObserver(() => setIsDark(root.classList.contains('dark')));
    observer.observe(root, { attributes: true, attributeFilter: ['class'] });
    return () => observer.disconnect();
  }, [theme]);
  return theme ?? (isDark ? 'dark' : 'light');
}

// Empty, size-reserving placeholder while the lazy Lottie chunk loads —
// deliberately no spinner, so no non-brand loader flashes behind the mark.
function Fallback({ pixels }: { pixels: number }) {
  return <div style={{ width: pixels, height: pixels }} />;
}

export function GlobalSpinner({ size = 'md', theme, className, speed }: Props) {
  const pixels = typeof size === 'number' ? size : SIZE_PIXELS[size];
  const resolvedTheme = useResolvedTheme(theme);
  return (
    <div role="status" aria-label="Loading" className={className}>
      <Suspense fallback={<Fallback pixels={pixels} />}>
        <LottieInner theme={resolvedTheme} pixels={pixels} speed={speed} />
      </Suspense>
    </div>
  );
}
